import { fbaConversionFactors } from "../components/fba/fbaConversionFactors";

/**
 * @param {string} fileContent PAS content
 * @param {'casing' | 'tubing'} pressureType
 * @param {'tight oil' | 'tight gas'} wellType
 * @param {'metric' | 'field'} units
 * @returns {Promise<Overview[]>} Overview parsed
 */
export async function parseFbaPAS(fileContent, pressureType, wellType, units) {
  return new Promise(async (resolve, reject) => {
    try {
      let reader = new FileReader();
        
      reader.readAsText(fileContent);

      reader.onload = async (event) => {
        const result = event.target.result;

        const jsBase = await parsePASFileIntoJS(result);
        const fbaProductionData = await extractOverview({ database: jsBase.jsObjects, timestamps: jsBase.timestamps, pressureType, wellType, units });
        resolve({rawData: fbaProductionData, fileName: fileContent?.name, fileSize: fileContent?.size / 1000});
      };
  
      reader.onerror = () => {
        reject({rawData: null, fileName: null, fileSize: null});
      };

    } catch(error) {
      console.log(error);
      reject([]);
    };
  });
}

/**
 * @typedef {object} JsBase
 * @property {object} jsObjects
 * @property {number[]} timestamps
 */

/**
 * @param {string} fileContent PAS content
 * @return {Promise<JsBase>} PAS converted in json
 */
async function parsePASFileIntoJS(fileContent) {
  const lines = fileContent.split(`\n`)
  const linesWithoutComments = lines.filter(l => !l.startsWith(`#`))

  
  let CURRENT_TABLE_NAME = ''
  let FIRST_ROW = true

  const record = {}

  for(const line of linesWithoutComments) {
    if (/^~DATA TABLE/gi.test(line)) {
      const [, rest] = line.split('DATA TABLE ')
      const [tableName, field, ,description] = rest.split(',')
      
      CURRENT_TABLE_NAME = tableName
      FIRST_ROW = true

      record[tableName] = record[tableName] || {}
      record[tableName] = {
        ...record[tableName],
        details: {
          ...record[tableName].details,
          [field]: description
        },
        columns: [],
        data: []
      }

      continue
    } else if (/^~DT/gi.test(line) && CURRENT_TABLE_NAME) {
      const [, ...values] = line.split(',')

      if (FIRST_ROW) {
        record[CURRENT_TABLE_NAME].columns = values
        FIRST_ROW = false
        continue;
      }

      record[CURRENT_TABLE_NAME].data = [
        ...record[CURRENT_TABLE_NAME].data,
        values.map(v => v ? v : null)
      ]
      continue;
    }
  }


  const timestamps = new Set()
  
  const jsObjects = Object.entries(record).reduce((acc, [table, value]) => {
    const { details, columns, data } = value

    const formattedData = data.map((row) => {

      const rowObject = row.reduce((acc, curr, index) => {
        const key = columns.at(index)
        if (key) {
          if (/time/gi.test(key)) {
            const timestamp = parseDateIntoTimestamp(curr);
            acc[key] = timestamp
            timestamps.add(timestamp)
          } else {
            acc[key] = curr;
          }
        }
        return acc;
      }, {})

      return rowObject
    })

    acc[table] = {
      details,
      data: formattedData
    }

    return acc;
  }, {})


  return { jsObjects, timestamps: [...timestamps] };
}


/**
 * @param {string} date Date in format '2021 07 31 1500:00'
 * @return {number} Returns timestamp
 */
function parseDateIntoTimestamp(dateString) {
  const formattedDate = dateString.replace(/(\d{4}) (\d{2}) (\d{2}) (\d{2})(\d{2}):(\d{2})/, '$1-$2-$3T$4:$5:$6');

  const date = new Date(formattedDate);

  const timestamp = date.getTime();

  return timestamp
}

/**
 * @typedef {object} Overview
 * @property {number} date
 * @property {number} days
 * @property {number} pwh
 * @property {number} gas
 * @property {number} oil
 * @property {number} water
 * @property {number} condensate
 * @property {number} salinity
 */

/**
 * @typedef {object} ExtractOverviewProps
 * @property {object} database Return of parsePASFileIntoJS {jsObjects}
 * @property {number[]} timestamps
 * @property {'casing' | 'tubing'} pressureType
 * @property {'tight oil' | 'tight gas'} wellType
 * @property {'metric' | 'field'} units
 */

/**
 * @param {ExtractOverviewProps} props
 * @returns {Promise<Overview[]>}
 */
async function extractOverview(props) {
  const { database, timestamps, pressureType, wellType, units } = props

  if (!['casing', 'tubing'].includes(pressureType)) throw new Error(`Invalid pressureType`)
  if (!['tight oil', 'tight gas'].includes(wellType)) throw new Error(`Invalid wellType`)
  if (!['metric', 'field'].includes(units)) throw new Error(`Invalid units`)

  timestamps.sort()

  const firstEntry = timestamps.at(0)

  const tableNames = Object.keys(database);

  // let gasTable = ''
  let liquidTable = '';
  let summaryTable = '';

  for (const tableName of tableNames) {
    if (/GAS/gi.test(tableName)) {
      // gasTable = tableName;
      continue;
    } else if (/LIQUID/gi.test(tableName)) {
      liquidTable = tableName;
      continue;
    } else if (/SUMMARY/gi.test(tableName)) {
      summaryTable = tableName;
      continue;
    };
  };

  let fbaProductionData = timestamps.map(timestamp => {
    let days = +differenceInDays(firstEntry, timestamp).toFixed(4);
    let pwh = 0;
    let gas = 0.00001;
    let oil = 0.00001;
    let water = 0.00001;
    let condensate = 0.00001;
    let salinity = 0;

    const summaryRow = database[summaryTable].data.find(row => row.TIME === timestamp);
    // const gasRow = database[gasTable].data.find(row => row.TIME === timestamp)
    const liquidRow = database[liquidTable].data.find(row => row.TIME === timestamp);

    // Handle fields extracted from summary
    if (summaryRow) {
      const { CSPS, TUPS, QTGAS, QTOIL, QTWTR, QTCON } = summaryRow;

      // Handle pwh
      if (pressureType === 'casing' && CSPS !== null) {
        pwh = +CSPS;
      } else if (pressureType === 'tubing' && TUPS !== null) {
        pwh = +TUPS;
      };

      // Handle gas
      if (QTGAS !== null) gas = +QTGAS

      // Handle oil
      if (QTOIL !== null && wellType === 'tight oil') oil = +QTOIL

      // Handle water
      if (QTWTR !== null) water = +QTWTR

      // Handle condensate
      if (wellType === 'tight oil') {
        if (QTCON !== null) condensate = +QTCON;
      } else if (wellType === 'tight gas') {
        if (QTOIL !== null) condensate = +QTOIL;
      };

      if (units === 'metric') {
        pwh = pwh / fbaConversionFactors.pwh;
        oil = oil / fbaConversionFactors.oil;
        gas = gas / fbaConversionFactors.gas;
        water = water / fbaConversionFactors.water;
        condensate = condensate / fbaConversionFactors.condensate;
      };
    };

    // Handle fields extracted from liquid
    if (liquidRow) {
      const { SALT } = liquidRow;

      // Handle salinity
      if (SALT !== null) salinity = +SALT;
    };

    return {
      date: timestamp,
      days,
      pwh,
      gas,
      oil,
      water,
      condensate,
      salinity,
    };
  });

  fbaProductionData = fbaProductionData?.filter(row => row.pwh > 0);

  fbaProductionData.forEach((row,i) => {
    const delta = i === 0 ? row.days : (row.days - fbaProductionData[i-1].days); // calculate delta in days

    // Fill missing salinity values
    if (i > 0 && !row.salinity > 0) {
      row.salinity = fbaProductionData?.slice(1, i)?.findLast(d => d.salinity > 0)?.salinity;
    };

    row.sqrtDays = Math.sqrt(row.days); // calculate sqrt(t)
    row.gor = (row?.oil > 0.0001 && row?.gas > 0.0001) ? ((1000 * row?.gas) / row?.oil) : 0; // calculate gor
    row.owr = (row?.water > 0.0001 && row?.oil > 0.0001) ? (row?.oil / row?.water) : 0; // calculate owr
    row.gwr = (row?.water > 0.0001 && row?.gas > 0.0001) ? (row?.gas / row?.water) : 0; // calculate gwr
    row.cgr = (row?.condensate > 0.0001 && row?.gas > 0.0001) ? (row?.condensate / (row?.gas / 1000)) : 0; // calculate cgr
    row.cumWater = (row?.water > 0 ? row?.water * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumWater : 0) // calculate cum water
    row.cumOil = (row?.oil > 0 ? row?.oil * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumOil : 0) // calculate cum oil
    row.cumGas = (row?.gas > 0 ? row?.gas * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumGas : 0) // calculate cum gas
    row.cumCondensate = (row?.condensate > 0 ? row?.condensate * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumCondensate : 0); // calculate cum condensate
  });

  return fbaProductionData;
};

/**
 * 
 * @param {number} timestamp1 
 * @param {number} timestamp2 
 * @returns {number}
 */
function differenceInDays(timestamp1, timestamp2) {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  const diffInMs = Math.abs(date2 - date1);

  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return diffInDays;
}